import React, { useState, useEffect } from "react";
import { callApiWithOptions, HttpMethod } from "src/services/apiService";
import styles from "./DialerLogs.module.css";

interface Props {
  phoneNumber: string;
}

interface DialerLog {
  logId: number;
  initiatedCallOn: string | null;
  callCompletedAt: string | null;
  numberToDial: string | null;
  queueName: string | null;
  timeQueued: string | null;
}

const formatLocal = (d: string | null): string => {
  if (!d) return "\u2014";
  return new Date(d).toLocaleString();
};

const formatPhone = (phone: string | null): string => {
  if (!phone) return "\u2014";
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    return `+1 ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `+1 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }
  return phone;
};

const calcDuration = (initiated: string | null, completed: string | null): string => {
  if (!initiated || !completed) return "\u2014";
  const diff = Math.round(
    (new Date(completed).getTime() - new Date(initiated).getTime()) / 1000,
  );
  return diff >= 0 ? `${diff}s` : "\u2014";
};

const TrustCallDialerLogs: React.FC<Props> = ({ phoneNumber }) => {
  const [expanded, setExpanded] = useState(false);
  const [logs, setLogs] = useState<DialerLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [expandedLogIdx, setExpandedLogIdx] = useState<number | null>(null);

  useEffect(() => {
    setLogs([]);
    setLoaded(false);
    setError("");
    setExpanded(false);
  }, [phoneNumber]);

  const handleToggle = async () => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);

    if (newExpanded && !loaded && phoneNumber) {
      setIsLoading(true);
      setError("");
      try {
        const resp = await callApiWithOptions({
          endpoint: `sysadmin/advanced-remediation/trustcall-dialer-logs?phoneNumber=${encodeURIComponent(phoneNumber)}`,
          method: HttpMethod.GET,
        });
        setLogs(resp?.dialerLogs || []);
        setLoaded(true);
      } catch (err: any) {
        setError(err.message || "Failed to load dialer logs");
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className={styles.section}>
      <div className={styles.header} onClick={handleToggle}>
        <h5 className={styles.title}>TrustCall Dialer Logs</h5>
        <span className={styles.chevron}>{expanded ? "\u25B2" : "\u25BC"}</span>
      </div>
      {expanded && (
        <div className={styles.body}>
          {isLoading && (
            <div className="text-center py-3">
              <span className="spinner-border spinner-border-sm me-2" />
              Loading...
            </div>
          )}
          {error && <div className="alert alert-danger mb-0">{error}</div>}
          {loaded && !isLoading && logs.length === 0 && (
            <div className="text-muted py-2">No TrustCall dialer logs found for this phone number.</div>
          )}
          {loaded && logs.length > 0 && (
            <div className="table-responsive">
              <table className={styles.dataTable}>
                <thead>
                  <tr>
                    <th>Log ID</th>
                    <th>Initiated</th>
                    <th>Duration</th>
                    <th>Array Device</th>
                    <th>Queue</th>
                    <th>Queued</th>
                    <th>Completed</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, idx) => (
                    <React.Fragment key={log.logId ?? idx}>
                      <tr>
                        <td>{log.logId}</td>
                        <td>{formatLocal(log.initiatedCallOn)}</td>
                        <td>{calcDuration(log.initiatedCallOn, log.callCompletedAt)}</td>
                        <td>{formatPhone(log.numberToDial)}</td>
                        <td>{log.queueName || "\u2014"}</td>
                        <td>{formatLocal(log.timeQueued)}</td>
                        <td>{formatLocal(log.callCompletedAt)}</td>
                        <td>
                          <button
                            className="btn btn-link btn-sm text-muted p-0"
                            style={{ fontSize: "11px" }}
                            onClick={() =>
                              setExpandedLogIdx(expandedLogIdx === idx ? null : idx)
                            }
                          >
                            JSON
                          </button>
                        </td>
                      </tr>
                      {expandedLogIdx === idx && (
                        <tr>
                          <td colSpan={8} className="p-0">
                            <pre className={styles.jsonPre}>
                              {JSON.stringify(log, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TrustCallDialerLogs;
